import ShareIcon from '@mui/icons-material/Share';
import { Autocomplete, Box, Button, Checkbox, Divider, FormControlLabel, IconButton, TextField, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from 'react-i18next';
import LoaderComp from '../../../../components/Loader/LoaderComp';
import WorkSpaceItemListFileComponentImage from '../../WorkSpaceItemListFileComponentImage';
import { ISharePanelState } from './ShareFileComp';
import { Close } from '@mui/icons-material';
import Edit from '@mui/icons-material/Edit';
import dayjs from 'dayjs';
import DatePickerWithLocale from '../../../../components/KeydriveComponents/DatePicker/DatePickerWithLocale';


export interface IShareFileCompDefault {
    fileExtension: string;
    fileName: string;
    hasWatermark: boolean;
    setHasWatermark: (set: boolean) => void;
    setSharePanelState: React.Dispatch<React.SetStateAction<ISharePanelState>>;
    isCalendar: boolean;
    changeIsCalendar(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void;
    dontExpire: boolean;
    setSelectedDate: React.Dispatch<React.SetStateAction<Date | undefined>>;
    selectedDate: Date | undefined;
    isAcess: boolean;
    changeIsAcess(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void;
    changeDontExpire(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean): void;
    usersMenu: string[];
    emails: string[];
    handleEmailsChange: (event: any, newValue: string[]) => void;
    waitConsult: boolean;
    waitSave: boolean;
    acessCount: string;
    setAcessCount: React.Dispatch<React.SetStateAction<string>>,
    onClose?: () => void
}

export const ShareFileCompDefault: React.FC<IShareFileCompDefault> = ({
    fileExtension,
    fileName,
    hasWatermark,
    setHasWatermark,
    setSharePanelState,
    isCalendar,
    changeIsCalendar,
    dontExpire,
    setSelectedDate,
    selectedDate,
    isAcess,
    changeIsAcess,
    changeDontExpire,
    usersMenu,
    emails,
    handleEmailsChange,
    waitConsult,
    waitSave,
    acessCount,
    setAcessCount,
    onClose
}) => {

    const { t } = useTranslation();

    const onFormatDate = (date?: Date): string => {
        return !date ? '' : date.getDate() + '/' + (date.getMonth() + 1) + '/' + (date.getFullYear() % 100);
    };

    if (waitSave || waitConsult) return <LoaderComp />

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ShareIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">{t("ShareFileComp.share")}</Typography>
                </Box>
                {onClose != null &&

                    <IconButton onClick={onClose}>
                        <Close sx={{ color: "#000000" }} />
                    </IconButton>
                }
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{
                textAlign: "left",
                font: "normal normal normal 14px/21px Segoe UI",
                letterSpacing: "0px",
                color: "#969696",
                opacity: 1,
                mb: 2
            }}>
                {t("ShareFileComp.firstTitle")}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                    component="div"
                    sx={{
                        border: "1px solid #D5D5D5",
                        borderRadius: "4px",
                        opacity: 1,
                        display: "flex",
                        width: "100%",
                        alignItems: 'center',
                        padding: 1
                    }}
                >
                    <WorkSpaceItemListFileComponentImage
                        boxLayout={false}
                        extension={fileExtension}
                        isFolder={false}
                        widthIcon='16px'
                    />
                    <Typography
                        sx={{
                            textAlign: "left",
                            font: "normal normal normal 14px/24px Segoe UI",
                            letterSpacing: "0px",
                            color: "#2D2D2D",
                            opacity: 1,
                            marginLeft: "5px",
                            overflow: "hidden",
                            overflowWrap: "break-word",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            maxWidth: "400px"
                        }}
                    >
                        {fileName}
                    </Typography>
                </Box>
            </Box>

            <Typography variant="subtitle1" sx={{
                textAlign: "left",
                font: "normal normal 600 14px/21px Segoe UI",
                letterSpacing: "0px",
                color: "#2D2D2D",
                opacity: 1,
            }}>
                {t("ShareFileComp.advancedOptions")}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={hasWatermark}
                            onChange={(_, checked) => {
                                if (checked) {
                                    setHasWatermark(true);
                                } else {
                                    setHasWatermark(false);
                                }
                            }}
                            sx={{
                                color: "#22638D",
                                '&.Mui-checked': {
                                    color: "#22638D",
                                },
                            }}
                        />
                    }
                    label={
                        <Typography sx={{
                            textAlign: "left",
                            font: "normal normal normal 14px/24px Segoe UI",
                            letterSpacing: "0px",
                            color: "#2D2D2D",
                            opacity: 1,
                        }}>
                            {t("ShareFileComp.hasWatermark")}
                        </Typography>
                    }
                />
                <Button
                    onClick={() => {
                        setSharePanelState({
                            default: false,
                            waterMark: true
                        })
                    }}
                    disabled={!hasWatermark}
                    variant="text"
                    sx={{
                        font: "normal normal normal 14px/21px Segoe UI",
                        textTransform: "none",
                        color: "#22638D"
                    }}
                    startIcon={<Edit />}>
                    {t("ShareFileComp.editWatermark")}
                </Button>
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isCalendar}
                            onChange={changeIsCalendar}
                            sx={{
                                color: "#22638D",
                                '&.Mui-checked': {
                                    color: "#22638D",
                                },
                            }}
                            disabled={dontExpire}
                        />
                    }
                    label={
                        <Typography sx={{
                            textAlign: "left",
                            font: "normal normal normal 14px/24px Segoe UI",
                            letterSpacing: "0px",
                            color: "#2D2D2D",
                            opacity: 1,
                        }}>
                            {t("ShareFileComp.sharetilDate")}
                        </Typography>
                    }
                />
                <DatePickerWithLocale
                    disablePast
                    disabled={!isCalendar || dontExpire}
                    value={dayjs(selectedDate)}
                    onChange={(newValue) => setSelectedDate(dayjs(dayjs(newValue)).toDate())}
                />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isAcess}
                            onChange={changeIsAcess}
                            sx={{
                                color: "#22638D",
                                '&.Mui-checked': {
                                    color: "#22638D",
                                },
                            }}
                            disabled={dontExpire}
                        />
                    }
                    label={
                        <Typography sx={{
                            textAlign: "left",
                            font: "normal normal normal 14px/24px Segoe UI",
                            letterSpacing: "0px",
                            color: "#2D2D2D",
                            opacity: 1,
                        }}>
                            {t("ShareFileComp.limitAccess")}
                        </Typography>
                    }
                />
                <TextField
                    size="small"
                    type="number"
                    sx={{ width: '150px' }}
                    disabled={!isAcess || dontExpire}
                    variant='standard'
                    onChange={e => setAcessCount(e.target.value)}
                    value={acessCount}
                />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={dontExpire}
                            onChange={changeDontExpire}
                            sx={{
                                color: "#22638D",
                                '&.Mui-checked': {
                                    color: "#22638D",
                                },
                            }}
                        />
                    }
                    label={
                        <Typography sx={{
                            textAlign: "left",
                            font: "normal normal normal 14px/24px Segoe UI",
                            letterSpacing: "0px",
                            color: "#2D2D2D",
                            opacity: 1,
                        }}>
                            {t("ShareFileComp.dontLimitAccess")}
                        </Typography>
                    }
                />
            </Box>

            <Typography variant="subtitle1" sx={{
                textAlign: "left",
                font: "normal normal 600 14px/21px Segoe UI",
                letterSpacing: "0px",
                color: "#2D2D2D",
                opacity: 1,
                mb: 1
            }}>
                {t("ShareFileComp.shareWith")}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Autocomplete
                    multiple
                    freeSolo 
                    limitTags={2}
                    id="multiple-limit-tags"
                    options={usersMenu}
                    getOptionLabel={(option) => option}
                    renderInput={(params) => (
                        <TextField variant='standard' {...params} />
                    )}
                    sx={{ width: "100%" }}
                    disablePortal
                    value={emails}
                    onChange={handleEmailsChange}
                />
            </Box>
        </>
    )

}